import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { yupToFormErrors, Formik, Form } from 'formik';
import { observer } from 'mobx-react';
import { rejectCodes, policyType as policyTypes } from '@ourbranch/lookups';
import { Grid } from '@material-ui/core';

import withDatePicker from 'core/components/with-date-picker';
import { Button } from 'core/components/button';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import Field from 'core/components/form/form.v2';
import { mapOfferInputToQuoteRequestInput } from 'core/helpers/quoter.service';
import { Home } from '../../../../../../quote/components/property-detail/home';
import { InitialRiskDetail } from '../../../../../../quote/components/initial-risk-detail';
import { vinSchema, homeSchema } from './validation-schema';

import useStyles from './resubmit-offer-modal.styles';

const fixableAutoError = rejectCodes.NO_CARS;
const fixableHomeError = rejectCodes.VERISK_NO360;

const ResubmitOfferModal = ({ rejectionData, onClose }) => {
  const classes = useStyles();
  const session = useContext(AuthContext);
  const { offer: offerStore, quote: quoteStore } = useStore();
  const [loading, setLoading] = useState(false);

  // Check if this is a farm policy
  const isFarmPolicy = offerStore?.offer?.quote?.policyType === policyTypes.Farm;

  const initialValues = {
    ...offerStore?.offer?.quote,
    VIN: rejectionData.code === fixableAutoError ? '' : undefined,
    ...(rejectionData.code === fixableHomeError && !isFarmPolicy
      ? {
          home: {
            basementSqFt: '',
            basementType: '',
            constructionType: '',
            exteriorWallType: '',
            garageCarCapacity: '',
            garageType: '',
            homeQuality: '',
            numFamiliesInDwelling: '',
            numFireplaces: '',
            numFullBathrooms: '',
            numHalfBathrooms: '',
            numStories: '',
            numWoodBurningStoves: '',
            purchaseDate: '',
            roofShape: '',
            roofType: '',
            roofYear: '',
            sqFt: '',
            typeOfHome: '',
            yearBuilt: '',
            ...offerStore?.offer?.quote?.home
          }
        }
      : {})
  };

  const validate = async (modalValues) => {
    try {
      if (rejectionData.code === fixableAutoError) {
        await vinSchema.validate(modalValues, { abortEarly: false });
      } else if (rejectionData.code === fixableHomeError && !isFarmPolicy) {
        await homeSchema.validate(modalValues, { abortEarly: false });
      }
      // Note: Farm policies may not need validation based on get-error-data.jsx
      // lines 443-447 which only apply homeSchema to Home policy types
    } catch (error) {
      const formErrors = yupToFormErrors(error);
      console.log({ formErrors });

      return formErrors;
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const newOfferId = await quoteStore.requestQuoteAction(
        mapOfferInputToQuoteRequestInput(values),
        session.allStates,
        true
      );
      if (newOfferId) {
        await offerStore.getOffer(newOfferId);
      }
    } catch (error) {
      console.error('Error during requote:', error);
      offerStore.handleError(error);
      setLoading(false);
    }
  };

  return (
    <BaseDialog size="lg" onClose={onClose} open>
      <Grid className={classes.container}>
        <Label className={classes.mainLabel} type="greenBig">
          {rejectionData.modalText}
        </Label>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          validateOnChange={true}
          validateOnBlur={true}
        >
          <Form>
            <Grid spacing={4} container justifyContent="space-between" className={classes.formContainer}>
              {rejectionData.code === fixableHomeError && !isFarmPolicy && <Home />}
              {rejectionData.code === fixableHomeError && isFarmPolicy && <InitialRiskDetail />}
              {rejectionData.code === fixableAutoError && (
                <Field type="string" id="VIN" name="VIN" mode="light" label="Vin Number" />
              )}
              <Grid className={classes.buttonContainer}>
                <Button className={classes.buttons} variant="contained" color="primary" type="submit" loading={loading}>
                  Get price
                </Button>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Grid>
    </BaseDialog>
  );
};

ResubmitOfferModal.propTypes = {
  rejectionData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

ResubmitOfferModal.defaultProps = {
  canOverrideWithoutSaving: false
};

export default observer(withDatePicker(ResubmitOfferModal));
